import { router } from "@inertiajs/react";
import { useEffect, useRef } from "react";
import { getCableConsumer } from "@/utils/cable";

const POLL_INTERVAL_MS = 2000;

type UseLiveReloadOptions = {
  channel: string;
  id: string;
  only: string[];
  enabled: boolean;
};

export function useLiveReload({ channel, id, only, enabled }: UseLiveReloadOptions) {
  const onlyRef = useRef(only);
  onlyRef.current = only;

  useEffect(() => {
    if (!enabled || !id) {
      return;
    }

    let pollTimer: number | undefined;
    let subscription: ReturnType<ReturnType<typeof getCableConsumer>["subscriptions"]["create"]> | null = null;

    const reload = () => {
      router.reload({ only: onlyRef.current, showProgress: false });
    };

    const startPolling = () => {
      if (pollTimer !== undefined) {
        return;
      }

      pollTimer = window.setInterval(reload, POLL_INTERVAL_MS);
    };

    const stopPolling = () => {
      if (pollTimer === undefined) {
        return;
      }

      window.clearInterval(pollTimer);
      pollTimer = undefined;
    };

    subscription = getCableConsumer().subscriptions.create(
      { channel, id },
      {
        received() {
          reload();
        },
        connected() {
          stopPolling();
          // Catch up if claim/step broadcasts fired before this tab subscribed.
          reload();
        },
        disconnected() {
          startPolling();
        },
        rejected() {
          startPolling();
        },
      },
    );

    return () => {
      stopPolling();
      subscription?.unsubscribe();
    };
  }, [channel, enabled, id]);
}
